export class FelixChessNavigation {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.navEl = options.navEl || null;
        this.replay = options.replay || null;
        this.context = options.context || 'default';
        this.soundController = options.soundController || null;

        this.unsubscribePositionChange = null;
        this.unsubscribeGameLoaded = null;
        this.unsubscribeSoundChange = null;

        this.onKeyDown = this.onKeyDown.bind(this);
        this.onNavClick = this.onNavClick.bind(this);
        this.onMouseDownFocus = this.onMouseDownFocus.bind(this);

        this.renderNav();
        this.bindNav();
        this.bindReplay();

        if (this.rootEl) {
            this.rootEl.addEventListener('mousedown', this.onMouseDownFocus);
        }
    }

    renderNav() {
        if (!this.navEl) return;

        let navInner = this.navEl.querySelector('[data-role="navInner"]');

        if (!navInner) {
            this.navEl.insertAdjacentHTML(
                'beforeend',
                `
                <div class="fc-boardViewer__navInner" data-role="navInner">
                    <button type="button" class="fc-boardViewer__navBtn" data-action="first" aria-label="Go to game start">&lt;&lt;</button>
                    <button type="button" class="fc-boardViewer__navBtn" data-action="prev" aria-label="Previous move">&lt;</button>
                    <div class="fc-boardViewer__navStatus" data-role="navStatus">0 / 0</div>
                    <button type="button" class="fc-boardViewer__navBtn" data-action="next" aria-label="Next move">&gt;</button>
                    <button type="button" class="fc-boardViewer__navBtn" data-action="last" aria-label="Go to game end">&gt;&gt;</button>
                    <button type="button" class="fc-boardViewer__navBtn fc-board-sound-toggle" data-action="sound-toggle" aria-label="Disable move sounds" title="Disable move sounds">🔊</button>
                </div>
                `
            );
        }

        this.updateSoundToggle();
    }

    bindReplay() {
        if (!this.replay) return;

        this.unsubscribeGameLoaded = this.replay.on('gameloaded', (state) => {
            this.setVisible(true);
            this.updateFromState(state);
            this.bindKeyboard();

            if (this.rootEl) {
                this.rootEl.focus();
            }
        });

        this.unsubscribePositionChange = this.replay.on('positionchange', (state) => {
            this.updateFromState(state);
        });

        if (this.soundController && typeof this.soundController.onChange === 'function') {
            this.unsubscribeSoundChange = this.soundController.onChange(() => {
                this.updateSoundToggle();
            });
        }
    }

    bindNav() {
        if (!this.navEl) return;
        this.navEl.addEventListener('click', this.onNavClick);
    }

    onNavClick(e) {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;

        switch (btn.getAttribute('data-action')) {
            case 'sound-toggle':
                if (this.soundController && typeof this.soundController.toggle === 'function') {
                    this.soundController.toggle();
                    this.updateSoundToggle();
                }
                break;
            case 'first':
                if (this.handleVariationNavigation('first')) return;
                if (!this.replay) return;
                this.replay.first('navigation', true);
                break;
            case 'prev':
                if (this.handleVariationNavigation('prev')) return;
                if (!this.replay) return;
                this.replay.prev('navigation', true);
                break;
            case 'next':
                if (this.handleVariationNavigation('next')) return;
                if (!this.replay) return;
                this.replay.next('navigation', true);
                break;
            case 'last':
                if (this.handleVariationNavigation('last')) return;
                if (!this.replay) return;
                this.replay.last('navigation', true);
                break;
        }
    }

    onKeyDown(e) {
        if (!this.replay) return;

        const state = this.replay.getState();
        if (!state.hasGame) return;

        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (this.handleVariationNavigation('prev')) return;
            this.replay.prev('navigation', true);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (this.handleVariationNavigation('next')) return;
            this.replay.next('navigation', true);
        }
    }

    handleVariationNavigation(action) {
        const variationInput = this.rootEl?.__fcVariationInputController;
        if (!variationInput || typeof variationInput.isActive !== 'function' || !variationInput.isActive()) {
            return false;
        }

        switch (action) {
            case 'first':
                variationInput.returnToMainline(true);
                return true;
            case 'prev':
                return variationInput.stepVariation(-1);
            case 'next':
                return variationInput.stepVariation(1);
            case 'last':
                return variationInput.goToVariationEnd();
            default:
                return false;
        }
    }

    onMouseDownFocus() {
        if (this.rootEl) {
            this.rootEl.focus();
        }
    }

    updateFromState(state) {
        this.updateStatus(state);
        this.updateButtons(state);
        this.setVisible(state.hasGame);
    }

    updateStatus(state) {
        if (!this.navEl) return;

        const statusEl = this.navEl.querySelector('[data-role="navStatus"]');
        if (!statusEl) return;

        if (state?.variationActive) {
            const current = Number.isInteger(state.activeVariationPly)
                ? state.activeVariationPly + 1
                : 1;
            const total = Number.isInteger(state.activeVariationLength) && state.activeVariationLength > 0
                ? state.activeVariationLength
                : current;
            statusEl.textContent = `Var ${current} / ${total}`;
            return;
        }

        statusEl.textContent = `${state.currentMove} / ${state.totalMoves}`;
    }

    updateButtons(state) {
        if (!this.navEl) return;

        const firstBtn = this.navEl.querySelector('[data-action="first"]');
        const prevBtn = this.navEl.querySelector('[data-action="prev"]');
        const nextBtn = this.navEl.querySelector('[data-action="next"]');
        const lastBtn = this.navEl.querySelector('[data-action="last"]');

        if (state?.variationActive) {
            const current = Number.isInteger(state.activeVariationPly)
                ? state.activeVariationPly
                : 0;
            const total = Number.isInteger(state.activeVariationLength) && state.activeVariationLength > 0
                ? state.activeVariationLength
                : current + 1;
            if (firstBtn) firstBtn.disabled = false;
            if (prevBtn) prevBtn.disabled = false;
            if (nextBtn) nextBtn.disabled = current >= total - 1;
            if (lastBtn) lastBtn.disabled = current >= total - 1;
            return;
        }

        if (firstBtn) firstBtn.disabled = state.atStart;
        if (prevBtn) prevBtn.disabled = state.atStart;
        if (nextBtn) nextBtn.disabled = state.atEnd;
        if (lastBtn) lastBtn.disabled = state.atEnd;
    }

    updateSoundToggle() {
        if (!this.navEl) return;

        const btn = this.navEl.querySelector('[data-action="sound-toggle"]');
        if (!btn) return;

        const enabled = !this.soundController || typeof this.soundController.isEnabled !== 'function'
            ? true
            : this.soundController.isEnabled();

        btn.textContent = enabled ? '🔊' : '🔇';
        btn.classList.toggle('is-on', enabled);
        btn.classList.toggle('is-off', !enabled);
        btn.setAttribute('aria-label', enabled ? 'Disable move sounds' : 'Enable move sounds');
        btn.setAttribute('title', enabled ? 'Disable move sounds' : 'Enable move sounds');
        btn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    }

    setVisible(visible) {
        if (!this.navEl) return;
        this.navEl.hidden = !visible;
    }

    bindKeyboard() {
        if (!this.rootEl) return;

        this.rootEl.removeEventListener('keydown', this.onKeyDown);
        this.rootEl.addEventListener('keydown', this.onKeyDown);
    }

    unbindKeyboard() {
        if (!this.rootEl) return;

        this.rootEl.removeEventListener('keydown', this.onKeyDown);
    }

    clear() {
        this.updateFromState({
            hasGame: false,
            currentMove: 0,
            totalMoves: 0,
            atStart: true,
            atEnd: true
        });
        this.unbindKeyboard();
    }

    destroy() {
        if (this.navEl) {
            this.navEl.removeEventListener('click', this.onNavClick);
        }

        if (this.rootEl) {
            this.rootEl.removeEventListener('mousedown', this.onMouseDownFocus);
        }

        if (this.unsubscribeGameLoaded) {
            this.unsubscribeGameLoaded();
            this.unsubscribeGameLoaded = null;
        }

        if (this.unsubscribePositionChange) {
            this.unsubscribePositionChange();
            this.unsubscribePositionChange = null;
        }

        if (this.unsubscribeSoundChange) {
            this.unsubscribeSoundChange();
            this.unsubscribeSoundChange = null;
        }

        this.unbindKeyboard();
    }
}
